/**
 * Translates raw PDM product records into Upholstery {@link Step} fragments.
 *
 * The live PDM JSON shape wasn't available in-repo; implementers should:
 *
 * 1. Call `/api/pdm-products` locally and inspect one product matching a vehicle SKU.
 * 2. Map PDM attributes into the fragments expected by RenderStepContent (substeps +
 *    category.optionValues, and factory match entries on `factory-match`).
 *
 * **Structured bridge (recommended for first integration):**
 * Configure PDM so each product exposes a blob (custom field / JSON extension) shaped like:
 *
 * ```json
 * {
 *   "tmiConfigurator": {
 *     "body": { "category": { "optionValues": [...] }, "substeps": [...] } },
 *     "rows": { ... },
 *     "build": { ... },
 *     "factory-match": { "factoryMatches": [...] }
 *   }
 * }
 * ```
 *
 * Field name `tmiConfigurator` can be overridden at runtime via
 * `(product as Record<string, unknown>).tmiConfigurator` — edit `readBridgeObject` below
 * to try alternate property names returned by your PDM tenancy.
 */

import type { Step } from "@views/Upholstery/types"
import type { PdmMergeStepKey } from "./mergePdmWithDatoSteps"

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

function unwrapJsonString(raw: unknown): unknown {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as unknown
    } catch {
      return null
    }
  }
  return raw
}

/**
 * Locate the Upholstery overlay object stored on the PDM product record.
 */
function readBridgeObject(
  product: Record<string, unknown>
): Record<string, unknown> | null {
  const aliasKeys = [
    "tmiConfigurator",
    "tmi_upholstery",
    "upholstery_config",
    "UpholsteryConfig",
    "Configurator",
  ]

  for (const k of aliasKeys) {
    if (!(k in product)) continue

    let raw = unwrapJsonString(product[k])
    if (!isPlainObject(raw)) continue

    /** Nested `{ upholstery: { body, rows, ... } }` */
    if (isPlainObject(raw.upholstery)) {
      raw = raw.upholstery as Record<string, unknown>
    }

    /** Nested `{ steps: { body, rows, ... } }` */
    if (isPlainObject(raw.steps)) {
      raw = raw.steps as Record<string, unknown>
    }

    return raw
  }

  return null
}

/** Best-effort: map bridge keys → step keys (`factory-match` vs `factoryMatch`). */
function normalizeBridgeKeys(
  bridge: Record<string, unknown>
): Partial<Record<PdmMergeStepKey, Partial<Step>>> {
  const out: Partial<Record<PdmMergeStepKey, Partial<Step>>> = {}

  const pick = (aliases: string[]): Partial<Step> | undefined => {
    for (const a of aliases) {
      const v = bridge[a]
      if (isPlainObject(v)) {
        return v as Partial<Step>
      }
      const parsed = unwrapJsonString(v)
      if (isPlainObject(parsed)) {
        return parsed as Partial<Step>
      }
    }
    return undefined
  }

  const body = pick(["body"])
  const rows = pick(["rows"])
  const build = pick(["build", "configuration", "Configuration"])
  const fm = pick(["factory-match", "factoryMatch"])

  if (body) out.body = body
  if (rows) out.rows = rows
  if (build) out.build = build
  if (fm) out["factory-match"] = fm

  return out
}

export function extractConfigurableStepsFromPdmProduct(
  product: unknown
): Partial<Record<PdmMergeStepKey, Partial<Step>>> | null {
  if (!isPlainObject(product)) {
    return null
  }

  const bridge = readBridgeObject(product)
  if (!bridge) {
    return null
  }

  const overlays = normalizeBridgeKeys(bridge)
  return Object.keys(overlays).length > 0 ? overlays : null
}
